import PropTypes from 'prop-types';
import { css } from '@emotion/react';

import { infoCenterLocales } from 'locale/locale.config';

import { cms } from 'lib/cms';

import { mobileOnly, tabletUp } from 'styles/utils';

import useLoadMoreArticles from 'hooks/useLoadMoreArticles';

import Container from 'components/shared/Container/Container';
import Text from 'components/shared/Text/Text';
import Button from 'components/shared/Button/Button';
import PageLayout from 'components/shared/PageLayout/PageLayout';
import NavbarSpacer from 'components/shared/Navbar/NavbarSpacer';
import Breakpoint from 'components/shared/Breakpoint/Breakpoint';
import InfoCenterNavbar from 'components/InfoCenter/InfoCenterNavbar';
import PostBreadcrumbs from 'components/shared/PostBreadcrums/PostBreadcrums';
import PromoCard from 'components/shared/Promo/PromoCard';
import { promosBaseCss } from 'components/shared/PromoCards/PromoCards.style';
import Flexbox from 'components/shared/Flexbox/Flexbox';
import {
  entriesShape,
  menuItemShape,
} from 'components/InfoCenter/InfoCenter.config';
import PageHead from 'components/shared/PageHead/PageHead';
import BottomCta from 'components/shared/BottomCta/BottomCta';

const PAGE_TITLE = 'UTI 101 with Dr. Nissa';
const PAGE_SLUG = 'dr-nissa';

// The amount of articles to show on each request
const ARTICLE_LIMIT = 25;

const pageWrapperCss = css`
  flex-grow: 1;

  ${mobileOnly} {
    margin-bottom: 70px;
  }

  ${tabletUp} {
    margin-bottom: 140px;
  }
`;

const articlesCss = css`
  margin-bottom: 40px;

  ${mobileOnly} {
    margin-top: 33px;
  }

  ${tabletUp} {
    margin-top: 57px;
  }
`;

function InfoCenterDrNissa({ entries, topMenuItems }) {
  const {
    articles,
    fetchingInProgress,
    loadMoreArticles,
    reachedArticleLimit,
  } = useLoadMoreArticles(entries, 'drNissa', ARTICLE_LIMIT);

  return (
    <>
      <PageHead title="UTI 101 with Dr. Nissa" />
      <PageLayout bottomPadding={0}>
        <NavbarSpacer />
        {/* Navbar */}
        <Breakpoint tablet desktop>
          <InfoCenterNavbar menuItems={topMenuItems} />
        </Breakpoint>

        {/* Breadcrumbs */}
        <Breakpoint mobile>
          <PostBreadcrumbs
            breadcrumbs={[{ title: PAGE_TITLE, slug: PAGE_SLUG }]}
          />
        </Breakpoint>
        <div css={pageWrapperCss}>
          <Container>
            {/* Title */}
            <Text component="h1" fontSize={3.2} fontWeight={500} serifFont>
              {PAGE_TITLE}
            </Text>

            {/* Articles */}
            <div css={[promosBaseCss, articlesCss]}>
              {articles.map(article => (
                <PromoCard key={article.slug} {...article} />
              ))}
            </div>

            {/* Load more button */}
            {!reachedArticleLimit && (
              <Flexbox justifyContent="center">
                <Button
                  variant="outlined"
                  onClick={loadMoreArticles}
                  showProgress={fetchingInProgress}
                  disabled={fetchingInProgress}
                >
                  Load more
                </Button>
              </Flexbox>
            )}
          </Container>
        </div>
        <BottomCta />
      </PageLayout>
    </>
  );
}

InfoCenterDrNissa.propTypes = {
  entries: PropTypes.shape(entriesShape),
  topMenuItems: PropTypes.arrayOf(PropTypes.shape(menuItemShape)),
};

export async function getStaticPaths() {
  const paths = infoCenterLocales.map(localeCode => ({
    params: { locale: localeCode },
  }));

  return {
    paths,
    fallback: false,
  };
}

export async function getStaticProps() {
  const entries = await cms.getDrNissaArticles(ARTICLE_LIMIT);
  const topMenuItems = await cms.getInfoCenterTopMenu();

  return {
    props: {
      entries,
      topMenuItems,
    },
  };
}

export default InfoCenterDrNissa;
